import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import { getOperation } from "../services/api";
import type { IOperationResponse } from "../services/api";
import { getEvents, safeCapture } from "../analytics";

/** Statuses past which an operation no longer changes (SSE can stop). */
const TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "canceled",
  "expired",
]);

export function isOperationNonTerminal(op: IOperationResponse): boolean {
  return !TERMINAL_STATUSES.has((op.status ?? "").toLowerCase());
}

export function hasNonTerminalOperations(ops: IOperationResponse[]): boolean {
  return ops.some(isOperationNonTerminal);
}

/** Apply an operation delta to a list (replace by id, otherwise prepend). */
export function upsertOperationInList(
  list: IOperationResponse[],
  op: IOperationResponse
): IOperationResponse[] {
  const idx = list.findIndex((o) => o.operationId === op.operationId);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = op;
    return next;
  }
  return [op, ...list];
}

function closeEventSource(
  es: EventSource | null,
  esRef: MutableRefObject<EventSource | null>
): void {
  if (!es) return;
  es.close();
  if (esRef.current === es) {
    esRef.current = null;
  }
}

/** EventSource cannot set headers; ngrok free needs this query flag instead. */
function buildOperationsSseUrl(linkId: string): string {
  const base = `${config.API_BASE_URL}/widget/links/${linkId}/operations/sse`;
  if (/ngrok(-free)?\.(app|dev)/i.test(config.API_BASE_URL)) {
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}ngrok-skip-browser-warning=true`;
  }
  return base;
}

export interface UseOperationSSEOptions {
  /**
   * Watch a single operation. Stream opens as soon as the id is known (even
   * before the first GET), and stays open while that op is missing or
   * non-terminal.
   */
  watchOperationId?: string;
}

/**
 * Keeps operation statuses live via Server-Sent Events.
 *
 * The caller owns the operations list and provides an `onUpsert` callback to
 * apply each delta. The stream is only kept open while at least one loaded
 * operation is non-terminal (or, in watch mode, while the watched operation is
 * unknown or non-terminal).
 */
export function useOperationSSE(
  linkId: string | null | undefined,
  operations: IOperationResponse[],
  onUpsert: (op: IOperationResponse) => void,
  onReconnect?: () => void,
  options?: UseOperationSSEOptions
): void {
  const watchOperationId = options?.watchOperationId;
  const esRef = useRef<EventSource | null>(null);
  const isFirstOpenRef = useRef(true);

  const operationsRef = useRef(operations);
  const onUpsertRef = useRef(onUpsert);
  const onReconnectRef = useRef(onReconnect);
  operationsRef.current = operations;
  onUpsertRef.current = onUpsert;
  onReconnectRef.current = onReconnect;

  const hasActiveOps = useMemo(() => {
    if (!watchOperationId) {
      return hasNonTerminalOperations(operations);
    }
    const op = operations.find((o) => o.operationId === watchOperationId);
    // Open immediately for a known id; close only once we know it is terminal.
    return !op || isOperationNonTerminal(op);
  }, [operations, watchOperationId]);

  useEffect(() => {
    if (!linkId || !hasActiveOps) {
      closeEventSource(esRef.current, esRef);
      return;
    }

    if (esRef.current) return;

    isFirstOpenRef.current = true;
    const url = buildOperationsSseUrl(linkId);
    const es = new EventSource(url);
    esRef.current = es;

    const maybeClose = (): void => {
      const ops = operationsRef.current;
      if (watchOperationId) {
        const op = ops.find((o) => o.operationId === watchOperationId);
        if (op && !isOperationNonTerminal(op)) {
          closeEventSource(es, esRef);
        }
        return;
      }
      if (!hasNonTerminalOperations(ops)) {
        closeEventSource(es, esRef);
      }
    };

    es.onopen = () => {
      if (!isFirstOpenRef.current) {
        if (watchOperationId && linkId) {
          void getOperation(linkId, watchOperationId, {
            raasSilentOffline: true,
          })
            .then((fetched) => onUpsertRef.current(fetched))
            .catch((err) =>
              console.error(
                "[useOperationSSE] reconnect: failed to refresh watched operation",
                err
              )
            );
        } else {
          onReconnectRef.current?.();
        }
      }
      isFirstOpenRef.current = false;
    };

    es.onmessage = (event: MessageEvent) => {
      try {
        const raw = event.data as string;
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;

        if (data?.type === "heartbeat") {
          maybeClose();
          return;
        }

        const updated = data as IOperationResponse;
        if (!updated?.operationId) return;

        if (watchOperationId && updated.operationId !== watchOperationId) {
          maybeClose();
          return;
        }

        safeCapture(getEvents().OPERATION_STATUS_CHANGED, {
          operation_id: updated.operationId,
          status: updated.status,
          funding_status: updated.fundingStatus,
          quote_id: updated.quoteId,
          send_amount: updated.sendAmount,
          receive_amount: updated.receiveAmount,
        });

        onUpsertRef.current(updated);
        maybeClose();
      } catch (e) {
        console.error("[useOperationSSE] message parse error", e);
      }
    };

    es.onerror = () => {
      console.warn("[useOperationSSE] connection issue (auto-retry)", linkId);
    };

    return () => {
      closeEventSource(es, esRef);
    };
  }, [linkId, hasActiveOps, watchOperationId]);
}
